import type React from 'react';
import { useState } from 'react';
import { Action } from '~/authorizations';
import Can from '~/authorizations/Can';
import Icon from '~/components2/Icon';
import type { RequestableLayer } from './../store';
import RequestStatusButton from './RequestStatusButton';
import UtilitiesPresent from './UtilitiesPresent';

const tdClasses = 'p-1';

interface RequestTableProps {
  title: string;
  description: string;
  rows: RequestableLayer[];
  tableType: 'dataRequested' | 'fiberDetailRequested' | 'asbuiltRequested';
  button?: React.ReactNode;
  notes?: string;
  'data-testid'?: string;
}

const RequestableTable = ({
  title,
  description,
  rows,
  tableType,
  button,
  notes,
  'data-testid': testId,
}: RequestTableProps) => {
  const [isTableOpen, setIsTableOpen] = useState(true);

  const getStatusText = (row: RequestableLayer): string => {
    const { status } = row;

    let text = '';

    if (status === 'pending') {
      text = `Requested ${row.createdAt || ''}`;
    } else if (status === 'requested') {
      text = `Latest update ${row.updatedAt || ''}`;
    } else if (status === 'complete') {
      text = `Request fulfilled ${row.resolutionDate || ''}`;
    }

    return text;
  };

  return (
    <div className="w-full" data-testid={testId}>
      <div
        onClick={() => setIsTableOpen((prev) => !prev)}
        className="flex cursor-pointer items-center gap-3 hover:bg-grey-20"
      >
        <Icon icon={isTableOpen ? 'chevronUp' : 'chevronDown'} />
        <div className="text-lg">{title}</div>
      </div>

      {isTableOpen && (
        <>
          <div className="mb-2 flex items-center justify-between">
            <div className="text-xs">{description}</div>
            {button}
          </div>

          <table className="w-full text-xs">
            <thead className="text-md font-bold">
              <tr className="border-b border-b-grey-20">
                <td className={tdClasses}>Name</td>
                <td className={tdClasses}>Type</td>
                <td className="w-1/3 overflow-x-clip truncate p-1">Request Status</td>
                <td className={tdClasses}>Utilities Present</td>
                <td className={tdClasses}>Latest Update</td>
                <td className={tdClasses}>Notes</td>
              </tr>
            </thead>

            <tbody>
              {rows.map((row) => {
                return (
                  <tr key={row.id} className="border-b border-b-grey-20">
                    <td className={tdClasses}>{row.name || ''}</td>
                    <td className={tdClasses}>{row.type || ''}</td>
                    <td className={tdClasses}>
                      <Can action={Action.RequestMaps}>
                        <RequestStatusButton tableType={tableType} row={row} />
                      </Can>
                    </td>
                    <td className={tdClasses}>
                      <UtilitiesPresent row={row} status={row.utilitiesPresent} />
                    </td>
                    <td className={tdClasses}>{getStatusText(row)}</td>
                    <td className={tdClasses}>{row.notes}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          {notes && (
            <div className="pt-1 text-2xs italic">
              <strong className="font-bold">Note: </strong>
              {notes}
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default RequestableTable;
